import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../common/reducers/initContext";
import { initMapFactory, initMultiMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory } from "../common/reducers/utilities";
import { aggregatingFetch } from "../common/hooks/networkHook";
import { ensureArray, groupBy } from "../common/utils/arrayUtilities";
import { markFetched, registerEntityBasedFetch } from "../common/hooks/initHook";

const key = a => a.id;

const memberAwardSlice = createSlice({
    name: 'memberAwards',
    initialState: {
        data: {}
    },
    reducers: {
        set: (state, action) => {
            action.payload.memberids.forEach(memberid => delete state.data[memberid]);
            const byMember = groupBy(ensureArray(action.payload.data), 'memberid');
            Object.entries(byMember).forEach(([memberid, awards]) => {
                state.data[memberid] = awards;
            });
        },
        addOrUpdate: (state, action) => {
            const toUpdate = ensureArray(action.payload);
            const byMember = groupBy(toUpdate, 'memberid');
            Object.entries(byMember).forEach(([ memberid, awards ]) => {
                const ids = new Set(awards.map(a => key(a)));
                state.data[memberid] = (state.data[memberid]?.filter(a => !ids.has(key(a))) ?? []).concat(awards);
            });
        },
        remove: (state, action) => {
            const toRemove = ensureArray(action.payload);
            const byMember = groupBy(toRemove, 'memberid');
            Object.entries(byMember).forEach(([ memberid, awards ]) => {
                if (state.data[memberid]) {
                    const ids = new Set(awards.map(a => key(a)));
                    state.data[memberid] = state.data[memberid].filter(a => !ids.has(key(a)));
                    if (state.data[memberid].length === 0) {
                        delete state.data[memberid]
                    }
                }
            });
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => memberAwardSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'staffAwards', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'staffAwards');
const fetchMemberAwards = aggregatingFetch(
    memberid => network => network.post('/api/v2/awardsForMembers', [memberid], data => memberAwardSlice.actions.set({ memberids: [memberid], data }), visualizeFailure('get')),
    memberids => network => network.post('/api/v2/awardsForMembers', memberids, data => memberAwardSlice.actions.set({ memberids, data }), visualizeFailure('get')),
    { primitiveKey: true }
);

export const createMemberAward = (network, ma) => network.post('/api/v1/memberAward', ma, memberAwardSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateMemberAward = (network, delta) => network.put('/api/v1/memberAward', delta, memberAwardSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeMemberAward = (network, id) => network.delete('/api/v1/memberAward/' + encodeURIComponent(id), memberAwardSlice.actions.remove, visualizeFailure('remove'));
// #end region

//#region selectors
const selectMemberAwardData = state => state.memberAwards.data;
const selectMemberAwards = memberid => createSelector([selectMemberAwardData], memberAwards => memberAwards[memberid] ?? [])
const selectAwardsForMembers = memberids => createSelector([selectMemberAwardData], memberAwards => {
    return memberids.reduce((res, memberid) => {
        res[memberid] = memberAwards[memberid] ?? [];
        return res;
    }, {});
})

// #endregion

const listen = {
    memberAwardCreated: (ma, { dispatch, websocket }) => {
        markFetched(websocket, 'member_award', key(ma));
        dispatch(memberAwardSlice.actions.addOrUpdate(ma))
    },
    memberAwardUpdated: (ma, { dispatch }) => dispatch(memberAwardSlice.actions.addOrUpdate(ma)),
    memberAwardRemoved: (ma, { dispatch }) => dispatch(memberAwardSlice.actions.remove(ma))
}

const fullRoles = ['administrator','member manager'];
const readRoles = ['auditor','member auditor','rostering manager','rostering auditor','recurring rostering manager','recurring rostering auditor','training manager','training auditor']
const options = {
    cleanup: { root: memberAwardSlice.name, all: state => Object.values(selectMemberAwardData), removeAll: memberAwardSlice.actions.remove, key },
    condition: credentials => {
        return credentials.hasRole(fullRoles.concat(readRoles))
    }
}

export const canUpdateMemberAward = (credentials, member) => checkPermissions(credentials, member, fullRoles, !member.inactive);
export const canCreateMemberAward = canUpdateMemberAward;
export const canRemoveMemberAward = canUpdateMemberAward;

const checkPermissions = (credentials, member, requiredRoles, condition) => {
    const access = credentials.hasRole(requiredRoles, {office: member?.office, client: member?.default_client, location: member?.default_location});
    return (access && condition !== false && !member?.inactive) ?? false 
}

const memberAwardInitByMemberID = initMapFactory(fetchMemberAwards, listen, { name: 'memberAwardInitByMemberID' });
registerEntityBasedFetch('member', memberAwardInitByMemberID)

export const [useMemberAwards, realizeMemberAwards] = dataHookWithCredentialsFactory(selectMemberAwards, memberAwardInitByMemberID, options);
export const [useAwardsForMembers] = dataHookWithCredentialsFactory(selectAwardsForMembers, initMultiMapFactory(memberAwardInitByMemberID, listen), options);

const reducer = { name: memberAwardSlice.name, reducer: memberAwardSlice.reducer };
export default reducer;